import { cn } from '../lib/cn';
import { MAP_GRID_COLS, MAP_GRID_ROWS } from './grid';
import { STATUS_FILL } from './status-styles';
import type { ArgentinaMapLabels, MapJurisdiction } from './types';

/**
 * The grid itself: one focusable <button> per jurisdiction, placed by
 * gridPosition. Hover and focus stay in sync through onEnter/onLeave; the
 * detail panel is the aria-live consumer of that state.
 */
export function MapGrid({
  jurisdictions,
  labels,
  active,
  onEnter,
  onLeave,
  onSelect,
  compact,
}: {
  jurisdictions: readonly MapJurisdiction[];
  labels: ArgentinaMapLabels;
  active: MapJurisdiction | null;
  onEnter: (jurisdiction: MapJurisdiction) => void;
  onLeave: () => void;
  onSelect?: (jurisdiction: MapJurisdiction) => void;
  compact: boolean;
}) {
  return (
    <div
      role="group"
      aria-label={labels.mapLabel}
      className="relative grid gap-1.5"
      style={{
        gridTemplateColumns: `repeat(${MAP_GRID_COLS}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${MAP_GRID_ROWS}, auto)`,
      }}
    >
      {jurisdictions.map((jurisdiction) => (
        <JurisdictionCell
          key={jurisdiction.id}
          jurisdiction={jurisdiction}
          statusLabel={labels.statuses[jurisdiction.status]?.label ?? jurisdiction.status}
          isActive={active?.id === jurisdiction.id}
          onEnter={() => onEnter(jurisdiction)}
          onLeave={onLeave}
          onSelect={onSelect ? () => onSelect(jurisdiction) : undefined}
          compact={compact}
        />
      ))}
    </div>
  );
}

function JurisdictionCell({
  jurisdiction,
  statusLabel,
  isActive,
  onEnter,
  onLeave,
  onSelect,
  compact,
}: {
  jurisdiction: MapJurisdiction;
  statusLabel: string;
  isActive: boolean;
  onEnter: () => void;
  onLeave: () => void;
  onSelect?: () => void;
  compact: boolean;
}) {
  const { row, col } = jurisdiction.gridPosition;
  return (
    <button
      type="button"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      onClick={onSelect}
      className={cn(
        'focus-ring flex items-center justify-center border font-mono uppercase tracking-wide transition-colors',
        compact ? 'h-9 text-[0.65rem] md:h-10' : 'h-12 text-xs md:h-14 md:text-[0.8rem]',
        STATUS_FILL[jurisdiction.status],
        isActive && 'ring-2 ring-offset-2 ring-offset-paper ring-accent',
        onSelect === undefined && 'cursor-default',
      )}
      style={{ gridColumn: col + 1, gridRow: row + 1 }}
      aria-label={`${jurisdiction.name} — ${statusLabel}`}
    >
      {jurisdiction.code}
    </button>
  );
}
